import React, { useEffect, useMemo } from 'react';
import { buildMatrixGrid, resolveRowTotalValue } from './matrixPivot';
import { formatMatrixMeasureCell } from './matrixCurrencyFormat';
import { buildMeasuresFromSummarized } from './matrixReportConfig';
import { matrixConfigHasDateGroups } from './matrixDateGrouping';
import DateGroupGranularityBar from './DateGroupGranularityBar';
import { useReportGroupPicklistValues } from './useReportGroupPicklistValues';
import {
  formatFormulaDisplayValue,
  getFormulaValueForGroup,
} from '../../../utils/formulaFields';
import './MatrixReportTable.css';
import { logReportCurrency } from '../debug/reportCurrencyDebug';
import { reportFieldIsCurrencyLike } from '../../../utils/reportCurrencyAggregate';

const TOTAL_COLUMN_LABEL = 'Total';

/**
 * Matrix (pivot) table — Total column is the last column (after column groups).
 */
const MatrixReportTable = ({
  previewData = [],
  rowGroups = [],
  columnGroups = [],
  selectedFields = [],
  summarizedFields = {},
  matrixDateGranularity = 'month',
  onMatrixDateGranularityChange,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
  recordCount = null,
  showTotals = true,
  matrixGrid = null,
  formulaFields = [],
  formulaSummarizedData = null,
}) => {
  const formulaColumns = (formulaFields || []).filter((f) => f?.field_name);
  const measures = useMemo(
    () => buildMeasuresFromSummarized(selectedFields, summarizedFields),
    [selectedFields, summarizedFields]
  );

  const reportFieldsForPicklist = useMemo(
    () => [...(selectedFields || []), ...(rowGroups || []), ...(columnGroups || [])],
    [selectedFields, rowGroups, columnGroups]
  );

  const matrixGroupFields = useMemo(
    () => [...(rowGroups || []), ...(columnGroups || [])],
    [rowGroups, columnGroups]
  );

  const picklistValuesByFieldId = useReportGroupPicklistValues(
    matrixGroupFields,
    reportFieldsForPicklist
  );

  const showDateGranularity = matrixConfigHasDateGroups(rowGroups, columnGroups);

  const pivotOptions = useMemo(
    () => ({
      matrixDateGranularity,
      baseCurrencyCode: multiCurrencyEnabled ? baseCurrencyCode : null,
      exchangeRateLookup: multiCurrencyEnabled ? exchangeRateLookup : null,
      reportFields: reportFieldsForPicklist,
      picklistValuesByFieldId,
    }),
    [
      matrixDateGranularity,
      baseCurrencyCode,
      exchangeRateLookup,
      multiCurrencyEnabled,
      reportFieldsForPicklist,
      picklistValuesByFieldId,
    ]
  );

  const currencyOptions = useMemo(
    () => ({
      baseCurrencyCode: multiCurrencyEnabled ? baseCurrencyCode : null,
      exchangeRateLookup: multiCurrencyEnabled ? exchangeRateLookup : null,
    }),
    [baseCurrencyCode, exchangeRateLookup, multiCurrencyEnabled]
  );

  const formatCell = (value, measure) =>
    formatMatrixMeasureCell(value, measure, measure.aggregation, currencyOptions);

  const grid = useMemo(() => {
    if (!previewData?.length) return null;
    // Always pivot client-side so amounts use exchange rates + display uses base currency
    // (API matrix grid is built without conversion — same as Report Data Viewer path).
    return buildMatrixGrid(previewData, rowGroups, columnGroups, measures, pivotOptions);
  }, [previewData, rowGroups, columnGroups, measures, pivotOptions]);

  useEffect(() => {
    if (!grid?.rowKeys?.length || !measures?.length) return;

    const sampleRow = previewData[0];
    const rowKey = grid.rowKeys[0];
    const measure = measures[0];
    const mid = measure.id || measure.field_name;
    const colKey = grid.columnKeys?.[0];
    const rawCell = grid.cells[rowKey]?.[colKey]?.[mid];
    const formatted = formatMatrixMeasureCell(
      rawCell,
      measure,
      measure.aggregation,
      currencyOptions
    );

    logReportCurrency('MatrixReportTable.pivot', {
      multiCurrencyEnabled,
      baseCurrencyCode: currencyOptions.baseCurrencyCode,
      hasLookup: Boolean(currencyOptions.exchangeRateLookup),
      measureCount: measures.length,
      measures: measures.map(m => ({
        id: m.id,
        field_name: m.field_name,
        data_type: m.data_type,
        aggregation: m.aggregation,
        isCurrencyLike: reportFieldIsCurrencyLike(m),
      })),
      previewRowCount: previewData.length,
      sampleRowCurrencyKeys: sampleRow
        ? Object.keys(sampleRow).filter(k => /currency|exchange|amount/i.test(k))
        : [],
      sampleRowFx: sampleRow
        ? {
            main_currency: sampleRow.main_currency,
            main_exchange_rate: sampleRow.main_exchange_rate,
            opportunity_currency: sampleRow.opportunity_currency,
            opportunity_exchange_rate: sampleRow.opportunity_exchange_rate,
          }
        : null,
      rawCellValue: rawCell,
      formattedCell: formatted,
      rowTotalRaw: grid.rowTotalCells?.[rowKey]?.[mid],
      rowTotalFormatted: formatMatrixMeasureCell(
        grid.rowTotalCells?.[rowKey]?.[mid],
        measure,
        measure.aggregation,
        currencyOptions
      ),
    });
  }, [grid, measures, previewData, currencyOptions, multiCurrencyEnabled]);

  if (!grid || !grid.rowKeys?.length) {
    return (
      <div className="matrix-report-empty">
        <p>
          No matrix data. Add row groups, column groups, and at least one summarized
          measure (or run preview).
        </p>
      </div>
    );
  }

  const { rowGroupFields, measures: measureList, rowLabels, columnLabels } = grid;
  const multiMeasure = measureList.length > 1;
  const detailCount =
    recordCount != null && Number.isFinite(Number(recordCount))
      ? Number(recordCount)
      : previewData?.length ?? 0;
  const grandTotalLabel =
    detailCount > 0 ? `Grand Total (${detailCount})` : 'Grand Total';

  const renderTotalCells = rowKey =>
    measureList.map(measure => {
      const mid = measure.id || measure.field_name;
      const value = resolveRowTotalValue(grid, rowKey, measure);
      return (
        <td key={`total-${rowKey}-${mid}`} className="matrix-row-total-cell">
          {formatCell(value, measure)}
        </td>
      );
    });

  const resolveFormulaGroupLabel = (rowLabel) => {
    if (!rowLabel) return '';
    if (rowLabel.label && rowLabel.label !== rowLabel.key) return rowLabel.label;
    if (rowLabel.parts?.length) return rowLabel.parts.join(' / ');
    return rowLabel.label || rowLabel.key || '';
  };

  const renderFormulaCells = (rowLabel, keyPrefix = 'f') => {
    const groupLabel = resolveFormulaGroupLabel(rowLabel);
    const labelCandidates = [
      groupLabel,
      rowLabel?.label,
      rowLabel?.key,
      ...(rowLabel?.parts || []),
    ].filter((v) => v != null && String(v).trim() !== '');

    return formulaColumns.map((ff) => {
      let raw = null;
      if (formulaSummarizedData) {
        for (const candidate of labelCandidates) {
          const v = getFormulaValueForGroup(formulaSummarizedData, ff, candidate);
          if (v !== null && v !== undefined) {
            raw = v;
            break;
          }
        }
      }
      return (
        <td
          key={`${keyPrefix}-${rowLabel?.key}-${ff.field_name}`}
          className="matrix-formula-cell"
        >
          {formatFormulaDisplayValue(raw, ff.data_type || 'percent')}
        </td>
      );
    });
  };

  const renderFormulaHeaders = () =>
    formulaColumns.map((ff) => (
      <th
        key={`fh-${ff.field_name}`}
        className="matrix-formula-header"
        rowSpan={multiMeasure ? 2 : 1}
      >
        {ff.field_label || ff.field_name}
      </th>
    ));

  return (
    <div className="matrix-report-table-wrap">
      {showDateGranularity && (
        <DateGroupGranularityBar
          rowGroups={rowGroups}
          columnGroups={columnGroups}
          matrixDateGranularity={matrixDateGranularity}
          onMatrixDateGranularityChange={onMatrixDateGranularityChange}
        />
      )}
      <table className="matrix-report-table">
        <thead>
          <tr>
            {rowGroupFields.map(f => (
              <th
                key={`rh-${f.id || f.field_name}`}
                className="matrix-row-header"
                rowSpan={multiMeasure ? 2 : 1}
              >
                {f.field_label || f.field_name}
              </th>
            ))}
            {columnLabels.map(col =>
              multiMeasure ? (
                <th
                  key={`ch-${col.key}`}
                  colSpan={measureList.length}
                  className="matrix-col-group-header"
                >
                  {col.label}
                </th>
              ) : (
                <th key={`ch-${col.key}`} className="matrix-col-header">
                  {col.label}
                </th>
              )
            )}
            {renderFormulaHeaders()}
            {showTotals && (
              <th
                className="matrix-row-total-header"
                rowSpan={multiMeasure ? 2 : 1}
                colSpan={multiMeasure ? measureList.length : 1}
              >
                {TOTAL_COLUMN_LABEL}
              </th>
            )}
          </tr>
          {multiMeasure && (
            <tr>
              {columnLabels.map(col =>
                measureList.map(measure => (
                  <th
                    key={`sub-${col.key}-${measure.id}`}
                    className="matrix-col-header matrix-sub-header"
                  >
                    {measure.field_label || measure.field_name}
                  </th>
                ))
              )}
              {showTotals &&
                measureList.map(measure => (
                  <th
                    key={`total-sub-${measure.id}`}
                    className="matrix-row-total-header matrix-sub-header"
                  >
                    {measure.field_label || measure.field_name}
                  </th>
                ))}
            </tr>
          )}
        </thead>
        <tbody>
          {rowLabels.map(rowLabel => (
            <tr key={`row-${rowLabel.key}`}>
              {rowGroupFields.map((f, idx) => (
                <td key={`rl-${rowLabel.key}-${f.id}`} className="matrix-row-label">
                  {rowLabel.parts[idx] ??
                    (idx === 0 && rowLabel.key === '__all__' ? 'All' : '')}
                </td>
              ))}
              {columnLabels.map(col =>
                measureList.map(measure => {
                  const mid = measure.id || measure.field_name;
                  const value = grid.cells[rowLabel.key]?.[col.key]?.[mid];
                  return (
                    <td
                      key={`cell-${rowLabel.key}-${col.key}-${mid}`}
                      className="matrix-value-cell"
                    >
                      {formatCell(value, measure)}
                    </td>
                  );
                })
              )}
              {renderFormulaCells(rowLabel)}
              {showTotals && renderTotalCells(rowLabel.key)}
            </tr>
          ))}
          {showTotals && (
            <tr className="matrix-grand-total-row">
              {rowGroupFields.map((f, idx) => (
                <td
                  key={`gt-label-${f.id}`}
                  className="matrix-row-label matrix-grand-label"
                >
                  {idx === 0 ? grandTotalLabel : ''}
                </td>
              ))}
              {columnLabels.map(col =>
                measureList.map(measure => {
                  const mid = measure.id || measure.field_name;
                  const value = grid.columnTotalCells[col.key]?.[mid];
                  return (
                    <td
                      key={`gt-${col.key}-${mid}`}
                      className="matrix-value-cell matrix-grand-total-cell"
                    >
                      {formatCell(value, measure)}
                    </td>
                  );
                })
              )}
              {formulaColumns.map((ff) => {
                const raw = formulaSummarizedData
                  ? getFormulaValueForGroup(formulaSummarizedData, ff, '__all__')
                  : null;
                return (
                  <td
                    key={`gt-formula-${ff.field_name}`}
                    className="matrix-formula-cell matrix-grand-total-cell"
                  >
                    {formatFormulaDisplayValue(raw, ff.data_type || 'percent')}
                  </td>
                );
              })}
              {measureList.map(measure => {
                const mid = measure.id || measure.field_name;
                const value = grid.grandTotalCells[mid];
                return (
                  <td
                    key={`gt-total-${mid}`}
                    className="matrix-row-total-cell matrix-grand-total-cell"
                  >
                    {formatCell(value, measure)}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MatrixReportTable;
